import type { AccountTransaction } from '@hero/schema';
import { formatDateMain } from '@hero/core';
import { useTranslation } from 'react-i18next';
import { AccountTransactionStatusBadge } from '../Status/AccountTransactionStatusBadge';
import { AccountTransactionTypeBadge } from '../Status/AccountTransactionTypeBadge';
import { DrawerContentList } from '../../../app/Drawer/DrawerContentList';
import { PriceBadge } from '../../../app/Price/PriceBadge';
import { Price } from '../../../app/Price';

export interface AccountTransactionSummaryTabProps {
  transaction: AccountTransaction;
}

export function AccountTransactionSummaryTab({ transaction }: AccountTransactionSummaryTabProps) {
  const { t } = useTranslation(['m-account-transaction', 'm-payment']);

  const flowLabel =
    transaction.flow === 1
      ? t('m-payment:transaction.flow.credit', { defaultValue: 'Credit' })
      : t('m-payment:transaction.flow.debit', { defaultValue: 'Debit' });

  const dateDisplay = transaction.createdAt
    ? formatDateMain({ format: 'date_time', date: transaction.createdAt })
    : undefined;

  const isFailed = transaction.status === 'failed';
  const isCredit = transaction.flow === 1;
  const priceColor = isFailed ? 'gray' : isCredit ? 'green' : 'red';

  const gatewayLabel = transaction.gateway?.method
    ? t(`m-payment:payment_methods.${transaction.gateway.method}`, {
        defaultValue: transaction.gateway.method,
      })
    : undefined;

  // The Account snapshot embedded on the tx already carries the
  // post-transaction balance — surface it directly so this drawer
  // doesn't need a separate Account fetch.
  const accountBalance = transaction.account.balance;

  return (
    <div className="flex flex-col px-5 py-2">
      <DrawerContentList
        list={[
          {
            label: t('m-account-transaction:fields.reference', {
              defaultValue: 'Reference',
            }),
            value: transaction.ref.main || transaction.ref.short,
          },
          {
            label: t('m-account-transaction:fields.status', { defaultValue: 'Status' }),
            value: <AccountTransactionStatusBadge status={transaction.status} />,
          },
          {
            label: t('m-account-transaction:fields.type', { defaultValue: 'Type' }),
            value: <AccountTransactionTypeBadge type={transaction.type} />,
          },
          {
            label: t('m-account-transaction:fields.flow', { defaultValue: 'Flow' }),
            value: flowLabel,
          },
          {
            label: t('m-account-transaction:fields.created_at', {
              defaultValue: 'Created at',
            }),
            value: dateDisplay ?? '-',
          },
          {
            label: t('m-account-transaction:fields.notes', { defaultValue: 'Notes' }),
            value: transaction.notes || '-',
            hidden: !transaction.notes,
          },
          {
            label: t('m-account-transaction:fields.amount', { defaultValue: 'Amount' }),
            value: (
              <PriceBadge
                price={Math.abs(transaction.amount)}
                currency={transaction.currency}
                color={priceColor}
              />
            ),
          },
          {
            label: t('m-account-transaction:fields.credit_amount', {
              defaultValue: 'Credit amount',
            }),
            value: <Price amount={transaction.creditAmount} currency={transaction.currency} />,
          },
          {
            label: t('m-account-transaction:fields.debit_amount', {
              defaultValue: 'Debit amount',
            }),
            value: <Price amount={transaction.debitAmount} currency={transaction.currency} />,
          },
          {
            label: t('m-account-transaction:fields.account_balance', {
              defaultValue: 'Account balance after',
            }),
            value: <Price amount={accountBalance} currency={transaction.currency} />,
          },
          {
            label: t('m-account-transaction:fields.gateway', { defaultValue: 'Gateway' }),
            value: gatewayLabel ?? '-',
            hidden: !gatewayLabel,
          },
        ]}
      />
    </div>
  );
}
